import { useEffect, useState } from 'react';

import dayjs from 'dayjs';

import useAnalytics from '@/utils/hooks/swr/useAnalytics';
import useSocialProfilePermissions from '@/utils/hooks/swr/useSocialProfilePermissions';
// Hooks
import useUser from '@/utils/hooks/swr/useUser';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import PropTypes from 'prop-types';

// Lib
import { MediaViewerStatsConfig } from '@/lib/analytics/mediaViewer';
import { SIZE_FULL } from '@/lib/gridElements/size';
import { CUSTOM } from '@/lib/periods';
import { POST, STORY } from '@/lib/posts/postTypes';
import {
    FACEBOOK,
    INSTAGRAM,
    LINKEDIN,
    PINTEREST,
    TIKTOK_BUSINESS,
    TWITTER,
    YOUTUBE
} from '@/lib/socialProfile';

// Components
import InfoBanner from '@/components/base/InfoBanner';
import { MediaViewerKPI } from '@/components/base/Kpi/MediaViewerKpi';
import ChartWrapper from '@/components/composite/MediaViewer/charts/ChartWrapper';
import AnalyticsGrid, { GRID_STYLES } from '@/components/layouts/wrappers/AnalyticsGrid';
import { Trans } from '@/components/tools/translation';
import MediaViewerEstimatedStatsBanner from '@/components/composite/MediaViewer/components/MediaViewerEstimatedStatsBanner';
import MediaViewerIncompleteStatsBanner from '@/components/composite/MediaViewer/components/MediaViewerIncompleteStatsBanner';

const KpiExcludedWhenNoFollowers = {
    [INSTAGRAM]: ['engagement_rate', 'reach_rate'],
    [FACEBOOK]: ['engagement_rate', 'reach_rate'],
    [LINKEDIN]: ['engagement_rate', 'impression_rate'],
    [PINTEREST]: ['engagement_rate', 'impression_rate'],
    [TWITTER]: ['engagement_rate'],
    [TIKTOK_BUSINESS]: ['engagement_rate', 'view_rate'],
    [YOUTUBE]: ['engagement_rate', 'view_rate']
};

const MediaViewerStats = ({
    post,
    socialNetworkType,
    socialNetworkId,
    postFormat = POST,
    isLoading = true
}) => {
    const { t } = useTranslation();
    const { user, isLoading: isUserLoading } = useUser({
        revalidateOnMount: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });
    const [paramsAnalyticsRequest, setParamsAnalyticsRequest] = useState(null);

    const statsFormatConfig = MediaViewerStatsConfig(socialNetworkType)[postFormat];

    const statsConfig =
        statsFormatConfig[isLoading ? Object.keys(statsFormatConfig)[0] : post?.type];

    const getAnalyticsFieldsAndParameters = () => {
        const fields = [];
        const parameters = {};
        const date =
            post?.created || post?.published_at
                ? dayjs(post?.created ?? post?.published_at).format('YYYY-MM-DD')
                : null;

        statsConfig
            ?.filter((config) => config.type === 'chart')
            ?.forEach((config) => {
                fields.push(config.id);
                parameters[config.id] = {
                    id_media: post?.id
                };
                if (config.defaultFilter) {
                    parameters[config.id].type = config.defaultFilter;
                }
            });

        return {
            period: CUSTOM,
            start: date,
            end: date,
            fields,
            parameters,
            disable_previous: true
        };
    };

    const {
        analytics,
        analyticsError,
        mutateAnalytics,
        isLoading: isLoadingAnalytics
    } = useAnalytics(socialNetworkId, paramsAnalyticsRequest, {
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });
    const { permissions, isLoading: isLoadingPermission } =
        useSocialProfilePermissions(socialNetworkId);

    useEffect(() => {
        if (post?.id && (post?.created || post?.published_at)) {
            setParamsAnalyticsRequest(getAnalyticsFieldsAndParameters());
        }
    }, [post?.created, post?.published_at, post?.id]);

    const getKpiSplit = (idKpi) => {
        const organic = post?.[`${idKpi}_organic`] ?? null;
        const paid = post?.[`${idKpi}_paid`] ?? null;
        if (organic || paid) {
            if (socialNetworkType === FACEBOOK) {
                return { organic_viral: organic, paid };
            }
            return { organic, paid };
        }
        return null;
    };

    const postFollowers = {
        value: post?.subscribers ?? post?.followers,
        estimated: post?.subscribers_estimated ?? post?.followers_estimated
    };

    return (
        <AnalyticsGrid gridStyle={GRID_STYLES.MEDIA_VIEWER}>
            {/* Warning banner if followers number isn't known for this post */}
            {post && postFollowers?.value === null && !isLoadingAnalytics ? (
                <AnalyticsGrid.Item
                    size={SIZE_FULL}
                    className="!min-h-0 rounded-m bg-icoGray-300 p-6 text-center font-GMedium text-xs text-icoGray-500 dark:bg-icoDarkMode-anthracite dark:text-icoDarkMode-beton">
                    <MediaViewerIncompleteStatsBanner
                        type={postFormat}
                        network={socialNetworkType}
                    />
                </AnalyticsGrid.Item>
            ) : null}
            {post && postFollowers?.estimated ? (
                <AnalyticsGrid.Item
                    size={SIZE_FULL}
                    className="!min-h-0 rounded-m bg-icoGray-300 p-6 text-center font-GMedium text-xs text-icoGray-500 dark:bg-icoDarkMode-anthracite dark:text-icoDarkMode-beton">
                    <MediaViewerEstimatedStatsBanner network={socialNetworkType} />
                </AnalyticsGrid.Item>
            ) : null}
            {statsConfig &&
                statsConfig.length > 0 &&
                statsConfig.map((config) =>
                    config.type === 'kpi' &&
                    (!KpiExcludedWhenNoFollowers?.[socialNetworkType].includes(config.id) ||
                        postFollowers?.value !== null) ? (
                        <AnalyticsGrid.Item
                            size={config.size}
                            gridStyle={GRID_STYLES.MEDIA_VIEWER}
                            key={config.id}>
                            <MediaViewerKPI
                                isLoading={isLoading || isUserLoading || isLoadingPermission}
                                data={
                                    !(isLoading || isUserLoading) && post
                                        ? {
                                              value: post[config?.id],
                                              split: getKpiSplit(config.id),
                                              data_estimated:
                                                  KpiExcludedWhenNoFollowers?.[
                                                      socialNetworkType
                                                  ].includes(config.id) && postFollowers?.estimated
                                          }
                                        : {}
                                }
                                config={{
                                    ...config,
                                    mergedData:
                                        socialNetworkType === FACEBOOK ||
                                        socialNetworkType === TIKTOK_BUSINESS ||
                                        socialNetworkType === YOUTUBE
                                            ? config?.mergedData
                                            : false,
                                    network: socialNetworkType,
                                    permissions
                                }}
                                userNumbersFormat={{
                                    number: user?.settings?.numbers_format ?? 'comma'
                                }}
                                variant="noShadow"
                                renderLoader={() => <config.loader />}
                            />
                        </AnalyticsGrid.Item>
                    ) : config.type === 'chart' ? (
                        <AnalyticsGrid.Item
                            size={config.size}
                            gridStyle={GRID_STYLES.MEDIA_VIEWER}
                            key={config.id}
                            className="min-h-[130px]">
                            <ChartWrapper
                                isLoading={isLoading || isLoadingAnalytics}
                                userNumbersFormat={{
                                    number: user?.settings?.numbers_format ?? 'comma'
                                }}
                                hasShadow={false}
                                data={
                                    analytics
                                        ? {
                                              ...config,
                                              ...analytics[config.id]
                                          }
                                        : {}
                                }>
                                <config.element />
                            </ChartWrapper>
                        </AnalyticsGrid.Item>
                    ) : null
                )}
            {analyticsError ? (
                <AnalyticsGrid.Item size={SIZE_FULL} className="!min-h-0">
                    <InfoBanner
                        title={t('errors:error_occured')}
                        message={
                            <Trans
                                i18nKey="analytics:error_data_fetching"
                                components={{
                                    tag0: (
                                        <button
                                            key="error"
                                            className="text-icoRed underline"
                                            onClick={mutateAnalytics}
                                        />
                                    )
                                }}
                            />
                        }
                    />
                </AnalyticsGrid.Item>
            ) : null}
        </AnalyticsGrid>
    );
};

MediaViewerStats.propTypes = {
    mediaId: PropTypes.string,
    post: PropTypes.object,
    socialNetworkType: PropTypes.string,
    socialNetworkId: PropTypes.number,
    postFormat: PropTypes.oneOf([POST, STORY]),
    isLoading: PropTypes.bool
};

export default MediaViewerStats;
